/**
 * Clinician Expansion Component
 *
 * Wrapper for inline clinician drill-down within the leadership provider heatmap.
 * Renders (top to bottom):
 *   1. Award history chart — Recharts line chart (% of max), hidden when no data
 *   2. Projected award badge — current quarter projected award
 *   3. ProgressDetailsCore(embedded=true) — full clinician view
 *
 * Breadcrumb tracking is delegated to ProgressDetailsCore which attaches
 * the clinician-level breadcrumb to the ProviderProfileBar element.
 *
 * Financial data boundary (XCUT-01): Award chart and projected award card render
 * HERE in the leadership wrapper, NOT inside ProgressDetailsCore.
 *
 * Phase 7: Leadership Inline Expansion (LEAD-01 through LEAD-06, XCUT-01)
 */

import { useEffect, useRef } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { AwardHistoryChart } from './AwardHistoryChart'
import { ProgressDetailsCore } from '@/views/equip/progress-details/ProgressDetailsCore'
import { useBreadcrumbs } from '@/views/equip/context/BreadcrumbContext'
import { useAwardHistory } from '@/hooks/equip/useEquipDashboard'

interface ClinicianExpansionProps {
  providerEmail: string
  providerName: string | null
  providerNpi: number | null
  calculatedAward: number | null
  year: number
  quarter: number
  onCollapse: () => void
  canSeeAwards?: boolean
}

export function ClinicianExpansion({
  providerEmail,
  providerName,
  providerNpi,
  calculatedAward,
  year,
  quarter,
  onCollapse,
  canSeeAwards = true,
}: ClinicianExpansionProps) {
  const { addLevel, removeLevel } = useBreadcrumbs()
  const expansionRef = useRef<HTMLDivElement>(null)
  const historyQuery = useAwardHistory(canSeeAwards && providerNpi ? String(providerNpi) : null)
  const awardHistory = historyQuery.data ?? []
  const historyLoading = historyQuery.isPending

  // Auto-scroll into viewport after mount (per CONTEXT.md)
  useEffect(() => {
    const timer = setTimeout(() => {
      expansionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 350)
    return () => clearTimeout(timer)
  }, [])

  // Register breadcrumb for clinician expansion (depth 1)
  useEffect(() => {
    const breadcrumbId = `clinician-${providerEmail}`
    addLevel({
      id: breadcrumbId,
      label: providerName || 'Unknown Clinician',
      depth: 1,
      onCollapse,
      scrollTarget: expansionRef.current || undefined,
    })

    return () => {
      removeLevel(breadcrumbId)
    }
  }, [providerEmail, providerName, onCollapse, addLevel, removeLevel])

  const hasFinancialContent = canSeeAwards && (awardHistory.length > 0 || calculatedAward !== null)

  return (
    <div ref={expansionRef}>
      {/* Award history chart (LEAD-04, LEAD-05, LEAD-06) — hidden when canSeeAwards is false */}
      {canSeeAwards && (
        historyLoading ? (
          <Skeleton className="h-[130px] w-full rounded-[var(--radius)] mx-4 my-3" />
        ) : awardHistory.length > 0 ? (
          <div className="px-4 pt-3">
            <AwardHistoryChart history={awardHistory} />
          </div>
        ) : null
      )}

      {/* Projected award badge — hidden when canSeeAwards is false */}
      {canSeeAwards && calculatedAward !== null && (
        <div className="px-4 pt-2 pb-1">
          <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5">
            <span className="text-xs text-muted-foreground">Current Projected Award:</span>
            <span className="text-sm font-semibold">${calculatedAward.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      )}

      {/* Divider between financial section and clinician view */}
      {hasFinancialContent && (
        <div className="border-b border-border/50 mx-4 my-2" />
      )}

      {/* ProgressDetailsCore (embedded) — no financial data inside (XCUT-01) */}
      <div className="p-2">
        <ProgressDetailsCore
          providerEmail={providerEmail}
          embedded
          initialYear={year}
          initialQuarter={quarter}
        />
      </div>
    </div>
  )
}
